import * as React from "react";
import {
  useCallback,
  memo,
  useEffect,
  useState,
  useRef,
  useMemo,
  lazy,
  Suspense,
} from "react";
import { useGlobalValue } from "@/stores/GlobalState";
import { useVipBadgeCount } from "@/hooks/useVipBadgeCount";
import { useT } from "@/i18n/hooks";
import {
  useHomeBottomNavItems,
  useHomeConfigSettled,
} from "@/modules/homeDisplay/hooks";
import utils from "@/utils";
import { SilentErrorBoundary } from "@/components/ChunkLoadBoundary/ChunkLoadBoundary";
import type {
  NavigationItem,
  BottomNavigationProps,
} from "../../BottomNavigation.types";
import "./BottomNavigation.scss";

// lottie-web(~300KB) + lottieData(~250KB) 一起占了首屏静态闭包的一大块，而底栏
// 在 configSettled 之前压根不渲染，后台配满 5 项时更是整套走 <img> 不碰 Lottie。
// 静态 import 等于让每个用户都为「可能用不到、且必定晚于首屏」的动画买单，
// 所以整块延后加载。fallback 用同 class 的等尺寸空盒，图标位不会跳。
const LottieIcon = lazy(() =>
  import("./LottieIcon").then(m => ({ default: m.LottieIcon }))
);

// 加载中 / 加载失败共用：与 LottieIcon 自己渲染的容器同 class（宽高 100%），
// 图标位不会跳，也不会塌陷带偏同一行的文案。
const lottieIconPlaceholder = (
  <div className="bottom-navigation__lottie-icon" />
);

// Invite(News) · Events(Games) · Home · Rewards · Account(Menu)
// 使用 Lottie 动画图标。
//
// ⚠️ 这是**没配后台**时的形态。后台「首页布局配置 → Bottom Nav Items」配了有效的 5 项时
// （useHomeBottomNavItems 只在恰好 5 项有效时返回非空），本 variant 也改用后台的顺序 + 图标
// （静态图，替代 Lottie 动画）；没配 / 配得不完整就整份回落这里，观感与改造前逐像素一致。
// 文案与徽标始终走前端（i18n + vipBadgeCount），后台只管图与顺序 —— 与底栏 template2 同口径。
const defaultItems: NavigationItem[] = [
  {
    id: "agent-center",
    name: "Invite",
    icon: "invite",
    useLottie: true,
    badge: "0",
  },
  {
    id: "event",
    name: "Promotions",
    icon: "event",
    useLottie: true,
    badge: "0",
  },
  {
    id: "home",
    name: "Home",
    icon: "home",
    useLottie: true,
    badge: "0",
  },
  {
    id: "vip",
    name: "Rewards",
    icon: "reward",
    useLottie: true,
  },
  {
    id: "profile",
    name: "Account",
    icon: "profile",
    useLottie: true,
    badge: "0",
  },
];

export const BottomNavigation: React.FC<BottomNavigationProps> = memo(
  ({
    className = "",
    activeItem = "home",
    onItemClick,
  }: BottomNavigationProps) => {
    const t = useT();
    const [vipBadgeCount] = useGlobalValue("vipBadgeCount");

    // 划入动画状态
    const [visible, setVisible] = useState(false);
    const hasAnimated = useRef(false);
    // 配置到位前整条栏都不渲染，避免先出内置图标、配置到达后再换一次皮。超时兜底见该 hook
    const configSettled = useHomeConfigSettled();

    // 首次加载后延迟触发划入动画。等配置落定再开始，划入的就直接是最终形态
    useEffect(() => {
      if (hasAnimated.current || !configSettled) return;
      const timer = setTimeout(() => {
        setVisible(true);
        hasAnimated.current = true;
      }, 150);
      return () => clearTimeout(timer);
    }, [configSettled]);

    // VIP 红点计数（首屏取值 + 域名容灾软恢复）统一在共享 hook 里，
    // 两套底栏模板逻辑逐字相同，分散写会漏改其中一个。
    useVipBadgeCount();

    const handleItemClick = useCallback(
      (item: NavigationItem) => {
        item.onClick?.();
        onItemClick?.(item.id);
      },
      [onItemClick]
    );

    // 后台下发的 5 项（无效 / 未配置时为空数组，见 useHomeBottomNavItems）
    const configuredItems = useHomeBottomNavItems();
    // 后台图加载失败（404 / 防盗链）的 navKey：逐项退回本套的 Lottie 图标，
    // 不整栏回落 —— 整栏回落会把「一张图坏了」放大成 5 项全变。
    const [failedIconKeys, setFailedIconKeys] = useState<Set<string>>(
      () => new Set()
    );

    const baseItems = useMemo<NavigationItem[]>(() => {
      if (configuredItems.length === 0) return defaultItems;
      // id 用 navKey：与 defaultItems 的 id 同一套取值（home/event/agent-center/vip/profile），
      // 故下面的 i18n switch、跳转分发、高亮判定全都不用改。
      return configuredItems.map(item => {
        const src = utils.getImageUrl(item.imageUrl);
        return {
          id: item.navKey,
          name: item.navKey,
          icon: item.navKey,
          useLottie: false,
          iconDefault: src,
          // 点击态图标可选：没配就沿用未点击那张（等于选中不换图）
          iconActive: item.activeImageUrl
            ? utils.getImageUrl(item.activeImageUrl)
            : src,
        };
      });
    }, [configuredItems]);

    const navigationItems = baseItems.map(item => {
      let translatedName = item.name;
      switch (item.id) {
        case "home":
          translatedName = t("navigation.home");
          break;
        case "event":
          translatedName = t("navigation.promotions");
          break;
        case "agent-center":
          translatedName = t("agent.invite");
          break;
        case "vip":
          translatedName = t("vip.rewards");
          break;
        case "profile":
          translatedName = t("navigation.account");
          break;
      }

      if (item.id === "vip") {
        return {
          ...item,
          name: translatedName,
          badge: vipBadgeCount > 0 ? String(vipBadgeCount) : undefined,
        };
      }
      return {
        ...item,
        name: translatedName,
        badge: undefined,
      };
    });

    const navClass = [
      "bottom-navigation",
      visible ? "bottom-navigation--visible" : "",
      className,
    ]
      .filter(Boolean)
      .join(" ");

    // 放在所有 hooks 之后再提前返回，避免 hooks 顺序变化
    if (!configSettled) return null;

    return (
      <nav className={navClass}>
        <div className="bottom-navigation__container">
          {navigationItems.map(item => {
            const isActive = activeItem === item.id;

            return (
              <button
                key={item.id}
                className={["bottom-navigation__item", isActive ? "active" : ""]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => handleItemClick(item)}
                aria-label={item.name}
                aria-current={isActive ? "page" : undefined}
              >
                {/*
                  后台配了图就用图（静态，选中态换 iconActive），否则用本套的 Lottie 动画。
                  单张图挂了 → 该项落回 Lottie，其余项不受影响。
                */}
                {item.useLottie === false && !failedIconKeys.has(item.id) ? (
                  <img
                    className="bottom-navigation__icon-img"
                    src={
                      (isActive ? item.iconActive : item.iconDefault) as string
                    }
                    alt=""
                    draggable={false}
                    onError={() =>
                      setFailedIconKeys(prev =>
                        prev.has(item.id) ? prev : new Set(prev).add(item.id)
                      )
                    }
                  />
                ) : (
                  /*
                    底栏在 ChunkLoadBoundary(router.tsx) 内部，动画 chunk 拉不到
                    会把整个 MainShell 换成错误卡片/整页刷新 —— 而图标只是装饰，
                    没了它文案、徽标、跳转全都照常。所以按 chunkReload.ts 注释里
                    「非关键 → SilentErrorBoundary」的归属静默掉，落到同一个空盒。
                  */
                  <Suspense fallback={lottieIconPlaceholder}>
                    <SilentErrorBoundary fallback={lottieIconPlaceholder}>
                      <LottieIcon
                        iconId={item.id}
                        isActive={isActive}
                        className="bottom-navigation__lottie-icon"
                      />
                    </SilentErrorBoundary>
                  </Suspense>
                )}
                <span className="bottom-navigation__label">{item.name}</span>
                {item.badge && item.badge !== "0" && (
                  <span className="bottom-navigation__badge">{item.badge}</span>
                )}
              </button>
            );
          })}
        </div>
      </nav>
    );
  }
);

BottomNavigation.displayName = "BottomNavigation";

export default BottomNavigation;
